import { useEffect, useState } from "react";
import { BUILD_ID, fetchServerVersion, isStaleBuild } from "@/lib/version";

/**
 * Detect when a newer deploy has landed.
 *
 * Polls `/version.json` on three triggers, all of which represent
 * moments when the user is actively engaging the page:
 *   1. Mount (initial check after hydration)
 *   2. Tab focus / `visibilitychange` to visible (most common trigger
 *      — user comes back to a long-idle tab)
 *   3. A `POLL_MS` interval timer (catches users who keep a tab focused
 *      for hours without alt-tabbing away, e.g. on a desktop monitor)
 *
 * Once `staleBuildId` is set, the timer + listeners stop firing — there's
 * nothing more to learn and we want to be cheap. The user either reloads
 * (problem solved) or dismisses the banner; either way we don't re-check.
 *
 * Failure mode is silent: if `fetchServerVersion` returns null (offline,
 * CDN hiccup) we simply don't update state. The hook never throws.
 */

const POLL_MS = 5 * 60 * 1000; // 5 minutes — same cadence Gmail uses.

export interface VersionCheckState {
  /** Server-reported buildId when it differs from ours. null = up to date. */
  staleBuildId: string | null;
}

export function useVersionCheck(): VersionCheckState {
  const [staleBuildId, setStaleBuildId] = useState<string | null>(null);

  useEffect(() => {
    // Dev / SSR / unknown build → no checks at all. Spares the dev-server
    // log noise and avoids triggering reloads on stories where we don't
    // have a stable buildId to compare against.
    if (BUILD_ID === "unknown" || typeof window === "undefined") return;

    let cancelled = false;

    async function check() {
      if (cancelled || staleBuildId) return;
      const server = await fetchServerVersion();
      if (cancelled || !server) return;
      if (isStaleBuild(server.buildId)) {
        setStaleBuildId(server.buildId);
      }
    }

    // Trigger 1: on mount
    void check();

    // Trigger 2: tab focus / visibility return
    function onVisibility() {
      if (document.visibilityState === "visible") void check();
    }
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onVisibility);

    // Trigger 3: periodic poll
    const timer = window.setInterval(check, POLL_MS);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onVisibility);
      window.clearInterval(timer);
    };
  }, [staleBuildId]);

  return { staleBuildId };
}
